import { ApolloClient } from "@apollo/client";
import { GET_PROMO_DETAIL } from "@/core/graphql/promotion/getPromoDetail";
import { PUT_PROMO_STATUS } from "@/core/graphql/promotion/putPromoStatus";
import { UPDATE_PROMO_PROGRAM } from "@/core/graphql/promotion/updatePromoProgram";
import { CREATE_PROMO_PROGRAM } from "@/core/graphql/promotion/createPromoProgram";
import {
  ICreatePromoProgramParams,
  IGetPromoDetailParams,
  IPromoDetail,
  IPromoDetailRepository,
  IPutPromoStatusParams,
  IUpdatePromoProgramParams,
} from "@/core/repositories/promotion/IPromoDetail.repository";
import { ApplicationError } from "@/infrastructure/errors/ApplicationError";

interface IGetPromoDetailQueryResponse {
  getPromoDetail: IPromoDetail | null;
}

interface IPutPromoStatusMutationResponse {
  putPromoStatus: IPromoDetail | null;
}

interface IUpdatePromoProgramMutationResponse {
  updatePromoProgram: IPromoDetail | null;
}

interface ICreatePromoProgramMutationResponse {
  createPromoProgram: IPromoDetail | null;
}

export class PromoDetailRepository implements IPromoDetailRepository {
  constructor(private readonly client: ApolloClient) {}

  async getPromoDetail(params: IGetPromoDetailParams, refetch = false): Promise<IPromoDetail> {
    if (refetch) {
      this.invalidatePromoDetailCache(params.promoEventId);
    }

    const { data } = await this.client.query<IGetPromoDetailQueryResponse>({
      query: GET_PROMO_DETAIL,
      variables: { promoEventId: params.promoEventId },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.getPromoDetail) {
      throw new ApplicationError("Failed to load promotion detail");
    }

    return this.normalizeDetail(data.getPromoDetail);
  }

  async putPromoStatus(params: IPutPromoStatusParams): Promise<IPromoDetail> {
    const { data, error } = await this.client.mutate<IPutPromoStatusMutationResponse>({
      mutation: PUT_PROMO_STATUS,
      variables: { params },
    });

    if (!data?.putPromoStatus) {
      throw new ApplicationError(error?.message ?? "Failed to update promotion status");
    }

    const normalized = this.normalizeDetail(data.putPromoStatus);
    this.invalidatePromoDetailCache(params.promoEventId);
    return normalized;
  }

  async updatePromoProgram(params: IUpdatePromoProgramParams): Promise<IPromoDetail> {
    const { data, error } = await this.client.mutate<IUpdatePromoProgramMutationResponse>({
      mutation: UPDATE_PROMO_PROGRAM,
      variables: { params },
    });

    if (!data?.updatePromoProgram) {
      throw new ApplicationError(error?.message ?? "Failed to update promotion details");
    }

    const normalized = this.normalizeDetail(data.updatePromoProgram);
    this.invalidatePromoDetailCache(params.promoEventId);
    return normalized;
  }

  async createPromoProgram(params: ICreatePromoProgramParams): Promise<IPromoDetail> {
    const { data, error } = await this.client.mutate<ICreatePromoProgramMutationResponse>({
      mutation: CREATE_PROMO_PROGRAM,
      variables: { params },
    });

    if (!data?.createPromoProgram) {
      throw new ApplicationError(error?.message ?? "Failed to create promotion");
    }

    const normalized = this.normalizeDetail(data.createPromoProgram);
    this.invalidatePromoDetailCache(normalized.promoEventId);
    return normalized;
  }

  private invalidatePromoDetailCache(promoEventId: number): void {
    this.client.cache.evict({
      id: "ROOT_QUERY",
      fieldName: "getPromoDetail",
      args: { promoEventId },
    });
    this.client.cache.gc();
  }

  private normalizeDetail(raw: IPromoDetail): IPromoDetail {
    return {
      ...raw,
      promoCode: raw.promoCode ?? null,
      fundingMethod: raw.fundingMethod ?? null,
      amountTolerance: raw.amountTolerance ?? null,
      dateTolerance: raw.dateTolerance ?? null,
      notes: raw.notes ?? null,
      startDate: raw.startDate ?? "",
      endDate: raw.endDate ?? "",
      matchBreakdown: raw.matchBreakdown ?? [],
      type: {
        promoTypeId: raw.type?.promoTypeId ?? 0,
        promoTypeName: raw.type?.promoTypeName ?? raw.type?.promoTypeKey ?? "",
        promoTypeCode: raw.type?.promoTypeCode ?? raw.type?.promoTypeKey ?? "",
        promoTypeKey: raw.type?.promoTypeKey ?? "",
      },
      status: {
        promoStatusId: raw.status?.promoStatusId ?? 0,
        promoStatusName: raw.status?.promoStatusName ?? raw.status?.promoStatusKey ?? "",
        promoStatusKey: raw.status?.promoStatusKey ?? "",
      },
      purchaser: {
        purchaserId: raw.purchaser?.purchaserId ?? 0,
        purchaserKey: raw.purchaser?.purchaserKey ?? "",
        purchaserName: raw.purchaser?.purchaserName ?? "",
      },
      retailer: raw.retailer?.retailerId
        ? {
            retailerId: raw.retailer.retailerId,
            retailerKey: raw.retailer.retailerKey ?? null,
            retailerName: raw.retailer.retailerName ?? null,
          }
        : null,
      lifecycle: {
        createdAt: raw.lifecycle?.createdAt ?? "",
        createdByName: raw.lifecycle?.createdByName ?? null,
        activatedAt: raw.lifecycle?.activatedAt ?? null,
        activatedByName: raw.lifecycle?.activatedByName ?? null,
        completedAt: raw.lifecycle?.completedAt ?? null,
        cancelledAt: raw.lifecycle?.cancelledAt ?? null,
      },
      financials: {
        committedBudget: raw.financials?.committedBudget ?? null,
        previouslyClaimed: raw.financials?.previouslyClaimed ?? null,
        remainingOpenBalance: raw.financials?.remainingOpenBalance ?? null,
      },
    };
  }
}
